import he from 'he';
import Big from 'big.js';
import { Map } from 'immutable';

import { newDetails, newInventory, newPersonalization, newProduct, newShipping, newVariations } from './new';
import { setProductErrors, updateProductVariationsErrors } from './errors';
import { convertVariations } from '../variations/convert';
import { getOversetValue } from './checkAttribute';
import { isValidNumber } from '../number';
import { getTagError } from '../validations/tags';
import { trimSpaces } from '../string';
import { makeValue } from './shapeAttributes';
import { getSize } from '../iterable/getSize';
import { get } from '../iterable/get';

import { ATTRIBUTE_DEFAULTS, CURRENCY, ERROR, NEW, OVERSET, REQUIRED, VALUE } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { MAX_NUMBER_OF_TAGS } from '../../constants/validations';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';
import { DATA } from '../../data';

export function convertPrice({ channel, currency, value }) {
  if (currency === CURRENCY.USD || !isValidNumber(value)) return value;

  const rate = get([channel, 'EXCHANGE_RATES', currency])(DATA);

  if (!rate) return value;

  return new Big(value).times(rate).toFixed(2, 0);
}

export function convertWeightToLbAndOz(weight) {
  try {
    const number = new Big(weight);
    const lb = number.div(16).round(0, 0);
    const oz = lb.abs().gt(0) ? number.minus(lb.times(16)) : number;
    return {
      lb: lb.toString(),
      oz: oz.round(4).toString(),
    };
  } catch (error) {
    return { lb: '', oz: '' };
  }
}

export function convertLbAndOzToWeight(pound, ounce) {
  let lb;
  let oz;

  try {
    lb = new Big(pound);
  } catch (error) {
    lb = undefined;
  }

  try {
    oz = new Big(ounce);
  } catch (error) {
    oz = undefined;
  }

  if (lb && oz) {
    return lb.times(16).plus(oz).round(4).toString();
  } else if (lb && !oz) {
    return lb.times(16).round(4).toString();
  } else if (!lb && oz) {
    return oz.round(4).toString();
  } else {
    return '';
  }
}

export function convertDescriptionToBodyHTML(description) {
  if (!description) return description;

  function mapListOrParagraphs(paragraph) {
    const content = paragraph.trim();
    if(content.startsWith('* ') || content.startsWith('- ')) {
      return `<div style="margin-left: 20px;">${he.encode(paragraph, { strict: true, useNamedReferences: true })}</div>`
    }
    return content
      ? `<p>${he.encode(paragraph, { strict: true, useNamedReferences: true })}</p>`
      : '<br>';
  }

  return description.split(/\r?\n/).map(mapListOrParagraphs).join('');
}

export function convertBodyHTMLToDescription(bodyHTML) {
  if (!bodyHTML || typeof bodyHTML !== 'string') return '';

  const element = document.createElement('div');
  element.innerHTML = bodyHTML
    .replace(/(<\/(p|li|td|th|h\d)>)+/g, '<br><br>')
    .replace(/<br[^>]*>/gi, '\n')
    .replace(/(^\n+)|<[^>]*>|<\/[^>]*>|(\n+$)/gi, '');

  // decode HTML entities
  return he.decode(element.innerText || element.textContent || '');
}

function filterTags(channel) {
  return function filter(tag) {
    return !getTagError({ channel, tag });
  };
}

export function convert({ from, to, type, value, ...rest }) {
  switch (type) {
    case ATTRIBUTE.DESCRIPTION: {
      switch (from) {
        case EBAY:
        case SHOPIFY: {
          switch (to) {
            case ETSY:
            case FAIRE: {
              return convertBodyHTMLToDescription(value);
            }

            default: {
              return value;
            }
          }
        }

        case ETSY:
        case FAIRE: {
          switch (to) {
            case EBAY:
            case SHOPIFY: {
              return convertDescriptionToBodyHTML(value);
            }

            default: {
              return value;
            }
          }
        }

        default: {
          return value;
        }
      }
    }

    case ATTRIBUTE.TAGS: {
      return value.filter(filterTags(to)).slice(0, MAX_NUMBER_OF_TAGS[to]);
    }

    case ATTRIBUTE.VARIATIONS: {
      return convertVariations({ from, to, value, ...rest });
    }

    default: {
      return value;
    }
  }
}

export function convertProduct({ from, to, product: source, shopData, taxonomyData }) {
  let product = source.set('channel', to);

  switch (from) {
    case EBAY: {
      switch (to) {
        case EBAY: {
          product = Map({
            channel: EBAY,
            productId: NEW,
            [FORM.DESCRIPTION]: product.get(FORM.DESCRIPTION),
            [FORM.DETAILS]: product.get(FORM.DETAILS),
            [FORM.INVENTORY]: product.get(FORM.INVENTORY),
            [FORM.PHOTOS]: product.get(FORM.PHOTOS),
            [FORM.SHIPPING]: product.get(FORM.SHIPPING),
            [FORM.TITLE]: product.get(FORM.TITLE),
            [FORM.VARIATIONS]: product.get(FORM.VARIATIONS),
            [FORM.VIDEOS]: product.get(FORM.VIDEOS),
          });

          if (product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, REQUIRED])) {
            product = product.setIn([FORM.INVENTORY, ATTRIBUTE.SKU, REQUIRED], false);
          }

          if (product.getIn([FORM.VARIATIONS, REQUIRED, ATTRIBUTE.SKU])) {
            product = product.setIn([FORM.VARIATIONS, REQUIRED, ATTRIBUTE.SKU], false);
          }

          if (product.getIn([FORM.SHIPPING, ATTRIBUTE.PAYMENT_POLICY, VALUE])) {
            product = product.setIn([FORM.SHIPPING, ATTRIBUTE.PAYMENT_POLICY, VALUE], null);
          }

          if (product.getIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE])) {
            product = product.setIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE], null);
          }

          if (product.getIn([FORM.SHIPPING, ATTRIBUTE.SHIPPING_POLICY, VALUE])) {
            product = product.setIn([FORM.SHIPPING, ATTRIBUTE.SHIPPING_POLICY, VALUE], null);
          }

          break;
        }

        case ETSY: {
          const description = makeValue(
            convert({
              from,
              to,
              type: ATTRIBUTE.DESCRIPTION,
              value: product.getIn([FORM.DESCRIPTION, VALUE]),
            }),
          );

          const details = newDetails({ channel: ETSY });
          const inventory = newInventory({ channel: ETSY })
            .setIn([ATTRIBUTE.PRICE, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]))
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const personalization = newPersonalization();
          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const shipping = newShipping({ channel: ETSY });
          const tags = makeValue(ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.TAGS]);
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const videos = makeValue(product.getIn([FORM.VIDEOS, VALUE]));

          product = newProduct({ channel: ETSY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PERSONALIZATION, personalization)
            .set(FORM.PHOTOS, photos)
            .set(FORM.SHIPPING, shipping)
            .set(FORM.TAGS, tags)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        case FAIRE: {
          const description = makeValue(
            convert({
              from,
              to,
              type: ATTRIBUTE.DESCRIPTION,
              value: product.getIn([FORM.DESCRIPTION, VALUE]),
            }),
          );

          const details = newDetails({ channel: FAIRE });
          const inventory = newInventory({ channel: FAIRE })
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.USD],
              product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE])
            )
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));

          product = newProduct({ channel: FAIRE })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.TITLE, title);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        case SHOPIFY: {
          const description = makeValue(product.getIn([FORM.DESCRIPTION, VALUE]));
          const details = newDetails({ channel: SHOPIFY });
          const inventory = newInventory({ channel: SHOPIFY })
            .setIn([ATTRIBUTE.PRICE, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]))
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const tags = makeValue(ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.TAGS]);
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const seo = Map({
            [ATTRIBUTE.META_DESCRIPTION]: Map({
              [OVERSET]: true,
              [VALUE]: getOversetValue({ attribute: ATTRIBUTE.META_DESCRIPTION, product }),
            }),
            [ATTRIBUTE.PAGE_TITLE]: Map({
              [OVERSET]: true,
              [VALUE]: getOversetValue({ attribute: ATTRIBUTE.PAGE_TITLE, product }),
            }),
          });

          const videos = makeValue(product.getIn([FORM.VIDEOS, VALUE]));

          product = newProduct({ channel: SHOPIFY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.SEO, seo)
            .set(FORM.TAGS, tags)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    case ETSY: {
      switch (to) {
        case EBAY: {
          const description = makeValue(
            convert({
              from,
              to,
              type: ATTRIBUTE.DESCRIPTION,
              value: product.getIn([FORM.DESCRIPTION, VALUE]),
            }),
          );

          const details = newDetails({ channel: EBAY });
          const inventory = newInventory({ channel: EBAY })
            .setIn([ATTRIBUTE.PRICE, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]))
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const shipping = newShipping({ channel: EBAY });
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const videos = makeValue(product.getIn([FORM.VIDEOS, VALUE]));

          product = newProduct({ channel: EBAY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.SHIPPING, shipping)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({
                from,
                to,
                type: ATTRIBUTE.VARIATIONS,
                product,
                shopData,
                taxonomyData,
                value: source.get(FORM.VARIATIONS),
              })
            ),
            shopData,
          });

          break;
        }

        case ETSY: {
          product = Map({
            channel: ETSY,
            productId: NEW,
            [FORM.DESCRIPTION]: product.get(FORM.DESCRIPTION),
            [FORM.DETAILS]: product.get(FORM.DETAILS),
            [FORM.INVENTORY]: product.get(FORM.INVENTORY),
            [FORM.PERSONALIZATION]: product.get(FORM.PERSONALIZATION),
            [FORM.PHOTOS]: product.get(FORM.PHOTOS),
            [FORM.TAGS]: product.get(FORM.TAGS),
            [FORM.TITLE]: product.get(FORM.TITLE),
            [FORM.VIDEOS]: product.get(FORM.VIDEOS),
          });

          if (source.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])) {
            product = product
              .set(FORM.FILES, source.get(FORM.FILES))
              .set(FORM.SHIPPING, newShipping({ channel: to }))
              .set(FORM.VARIATIONS, newVariations({ channel: to }));
          } else {
            product = product
              .set(FORM.SHIPPING, source.get(FORM.SHIPPING))
              .set(FORM.VARIATIONS, source.get(FORM.VARIATIONS));
          }

          if (product.getIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_ID, VALUE])) {
            product = product
              .setIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_ID, VALUE], null)
              .setIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_TYPE, VALUE], null);
          }

          if (getSize(product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE])) > 0) {
            product = product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE], EMPTY.LIST);
          }

          if (product.getIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE])) {
            product = product.setIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE], null);
          }

          if (product.getIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE])) {
            product = product.setIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE], null);
          }

          break;
        }

        case FAIRE: {
          const description = makeValue(product.getIn([FORM.DESCRIPTION, VALUE]));
          const details = newDetails({ channel: FAIRE });
          const inventory = newInventory({ channel: FAIRE })
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.USD],
              product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE])
            )
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));

          product = newProduct({ channel: FAIRE })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.TITLE, title);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        case SHOPIFY: {
          const description = makeValue(
            convert({
              from,
              to,
              type: ATTRIBUTE.DESCRIPTION,
              value: product.getIn([FORM.DESCRIPTION, VALUE]),
            }),
          );

          const details = newDetails({ channel: SHOPIFY });
          const inventory = newInventory({ channel: SHOPIFY })
            .setIn([ATTRIBUTE.PRICE, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]))
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const seo = Map({
            [ATTRIBUTE.META_DESCRIPTION]: Map({
              [OVERSET]: true,
              [VALUE]: trimSpaces(product.getIn([FORM.DESCRIPTION, VALUE])),
            }),
            [ATTRIBUTE.PAGE_TITLE]: Map({
              [OVERSET]: true,
              [VALUE]: product.getIn([FORM.TITLE, VALUE]),
            }),
          });

          const tags = Map({
            [VALUE]: convert({
              from,
              to,
              type: ATTRIBUTE.TAGS,
              value: product.getIn([FORM.TAGS, VALUE], EMPTY.ORDERED_MAP),
            }),
            [NEW]: convert({
              from,
              to,
              type: ATTRIBUTE.TAGS,
              value: product.getIn([FORM.TAGS, NEW], EMPTY.ORDERED_MAP),
            }),
          });

          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const videos = makeValue(product.getIn([FORM.VIDEOS, VALUE]));

          product = newProduct({ channel: SHOPIFY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.SEO, seo)
            .set(FORM.TAGS, tags)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    case FAIRE: {
      switch (to) {
        case EBAY: {
          const description = makeValue(
            convert({
              from,
              to,
              type: ATTRIBUTE.DESCRIPTION,
              value: product.getIn([FORM.DESCRIPTION, VALUE]),
            }),
          );

          const details = newDetails({ channel: EBAY });
          const inventory = newInventory({ channel: EBAY })
            .setIn([ATTRIBUTE.PRICE, VALUE],
              product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.USD])
            )
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const shipping = newShipping({ channel: EBAY });
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const videos = makeValue(ATTRIBUTE_DEFAULTS[EBAY][ATTRIBUTE.VIDEOS]);

          product = newProduct({ channel: EBAY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.SHIPPING, shipping)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({
                from,
                to,
                type: ATTRIBUTE.VARIATIONS,
                product,
                shopData,
                taxonomyData,
                value: source.get(FORM.VARIATIONS),
              })
            ),
            shopData,
          });

          break;
        }

        case ETSY: {
          const description = makeValue(product.getIn([FORM.DESCRIPTION, VALUE]));
          const details = newDetails({ channel: ETSY });
          const inventory = newInventory({ channel: ETSY })
            .setIn([ATTRIBUTE.PRICE, VALUE],
              product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.USD])
            )
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const personalization = newPersonalization();
          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const shipping = newShipping({ channel: ETSY });
          const tags = makeValue(ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.TAGS]);
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const videos = makeValue(ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.VIDEOS]);

          product = newProduct({ channel: ETSY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PERSONALIZATION, personalization)
            .set(FORM.PHOTOS, photos)
            .set(FORM.SHIPPING, shipping)
            .set(FORM.TAGS, tags)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        case FAIRE: {
          product = Map({
            channel: FAIRE,
            productId: NEW,
            [FORM.DESCRIPTION]: product.get(FORM.DESCRIPTION),
            [FORM.DETAILS]: product.get(FORM.DETAILS),
            [FORM.INVENTORY]: product.get(FORM.INVENTORY),
            [FORM.PHOTOS]: product.get(FORM.PHOTOS),
            [FORM.SHIPPING]: product.get(FORM.SHIPPING),
            [FORM.TITLE]: product.get(FORM.TITLE),
            [FORM.VARIATIONS]: product.get(FORM.VARIATIONS),
          });

          break;
        }

        case SHOPIFY: {
          const description = makeValue(
            convert({
              from,
              to,
              type: ATTRIBUTE.DESCRIPTION,
              value: product.getIn([FORM.DESCRIPTION, VALUE]),
            }),
          );

          const details = newDetails({ channel: SHOPIFY });
          const inventory = newInventory({ channel: SHOPIFY })
            .setIn([ATTRIBUTE.PRICE, VALUE],
              product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.USD])
            )
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const tags = makeValue(ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.TAGS]);
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const seo = Map({
            [ATTRIBUTE.META_DESCRIPTION]: Map({
              [OVERSET]: true,
              [VALUE]: trimSpaces(product.getIn([FORM.DESCRIPTION, VALUE])),
            }),
            [ATTRIBUTE.PAGE_TITLE]: Map({
              [OVERSET]: true,
              [VALUE]: product.getIn([FORM.TITLE, VALUE]),
            }),
          });

          const videos = makeValue(ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.VIDEOS]);

          product = newProduct({ channel: SHOPIFY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.SEO, seo)
            .set(FORM.TAGS, tags)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    case SHOPIFY: {
      switch (to) {
        case EBAY: {
          const description = makeValue(product.getIn([FORM.DESCRIPTION, VALUE]));
          const details = newDetails({ channel: EBAY });
          const inventory = newInventory({ channel: EBAY })
            .setIn([ATTRIBUTE.PRICE, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]))
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const shipping = newShipping({ channel: EBAY });
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const videos = makeValue(product.getIn([FORM.VIDEOS, VALUE]));

          product = newProduct({ channel: EBAY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.SHIPPING, shipping)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({
                from,
                to,
                type: ATTRIBUTE.VARIATIONS,
                product,
                shopData,
                taxonomyData,
                value: source.get(FORM.VARIATIONS),
              })
            ),
            shopData,
          });

          break;
        }

        case ETSY: {
          const description = makeValue(
            convert({
              from,
              to,
              type: ATTRIBUTE.DESCRIPTION,
              value: product.getIn([FORM.DESCRIPTION, VALUE]),
            }),
          );

          const details = newDetails({ channel: ETSY });
          const inventory = newInventory({ channel: ETSY })
            .setIn([ATTRIBUTE.PRICE, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]))
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const personalization = newPersonalization();
          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const tags = Map({
            [VALUE]: convert({
              from,
              to,
              type: ATTRIBUTE.TAGS,
              value: product.getIn([FORM.TAGS, VALUE], EMPTY.ORDERED_MAP),
            }),
            [NEW]: convert({
              from,
              to,
              type: ATTRIBUTE.TAGS,
              value: product.getIn([FORM.TAGS, NEW], EMPTY.ORDERED_MAP),
            }),
          });

          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));
          const videos = makeValue(product.getIn([FORM.VIDEOS, VALUE]));

          product = newProduct({ channel: ETSY })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PERSONALIZATION, personalization)
            .set(FORM.PHOTOS, photos)
            .set(FORM.TAGS, tags)
            .set(FORM.TITLE, title)
            .set(FORM.VIDEOS, videos);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        case FAIRE: {
          const description = makeValue(
            convert({
              from,
              to,
              type: ATTRIBUTE.DESCRIPTION,
              value: product.getIn([FORM.DESCRIPTION, VALUE]),
            }),
          );

          const details = newDetails({ channel: FAIRE });
          const inventory = newInventory({ channel: FAIRE })
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.USD],
              product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE])
            )
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          const photos = makeValue(product.getIn([FORM.PHOTOS, VALUE]));
          const title = makeValue(product.getIn([FORM.TITLE, VALUE]));

          product = newProduct({ channel: FAIRE })
            .set(FORM.DESCRIPTION, description)
            .set(FORM.DETAILS, details)
            .set(FORM.INVENTORY, inventory)
            .set(FORM.PHOTOS, photos)
            .set(FORM.TITLE, title);

          product = updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              convert({ from, to, type: ATTRIBUTE.VARIATIONS, product, shopData, value: source.get(FORM.VARIATIONS) })
            ),
            shopData,
          });

          break;
        }

        case SHOPIFY: {
          product = Map({
            channel: SHOPIFY,
            productId: NEW,
            [FORM.DESCRIPTION]: product.get(FORM.DESCRIPTION),
            [FORM.DETAILS]: product.get(FORM.DETAILS),
            [FORM.INVENTORY]: product.get(FORM.INVENTORY),
            [FORM.PHOTOS]: product.get(FORM.PHOTOS),
            [FORM.SEO]: product.get(FORM.SEO),
            [FORM.TAGS]: product.get(FORM.TAGS),
            [FORM.TITLE]: product.get(FORM.TITLE),
            [FORM.VARIATIONS]: product.get(FORM.VARIATIONS),
            [FORM.VIDEOS]: product.get(FORM.VIDEOS),
          });

          if (getSize(product.getIn([FORM.DETAILS, ATTRIBUTE.COLLECTIONS, VALUE])) > 0) {
            product = product.setIn([FORM.DETAILS, ATTRIBUTE.COLLECTIONS, VALUE], EMPTY.LIST);
          }

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    default: {
      break;
    }
  }

  return product.has(ERROR)
    ? setProductErrors({ product, shopData, taxonomyData })
    : product;
}
